import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { dasherize } from '@ember/string';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import AnimatedContainer from 'ember-animated/components/animated-container';
import AnimatedValue from 'ember-animated/components/animated-value';
import { toLeft, toRight } from 'ember-animated/transitions/move-over';
import type { EmberChangeset } from 'ember-changeset';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import type { IntlService } from 'ember-intl';
import eq from 'ember-truth-helpers/helpers/eq';
import Button from 'ember-wiser/components/button';
import Form from 'ember-wiser/components/form';
import { type Questions as MiciQuestions } from 'parog-web/components/mici/base-edition-form';
import Summary from 'parog-web/components/multi-steps-edition-form/summary';
import { sleep } from 'parog-web/utils/time';

interface Signature {
  Blocks: {
    title: [];
  };
  Args: {
    currentSections: Array<string>;
    save: () => void;
    changeset: EmberChangeset;
    questions: typeof MiciQuestions;
    translationPrefix: string;
  };
}

export default class MultiStepsEditionForm extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked currentIndex = 0;

  duration = 300;

  get currentQuestionKey(): string {
    return this.args.currentSections[this.currentIndex];
  }

  get editionFormComponent() {
    return this.args.questions[dasherize(this.currentQuestionKey)];
  }

  get isOnSummary(): boolean {
    return this.currentIndex == this.args.currentSections.length;
  }

  get #conditionalQuestionKeys(): string[] {
    return [
      'bloodInStool',
      'immediateStool',
      'analIncontinence',
      'articularPain',
      'eyeSymptoms',
      'fever',
      'abdominalPain',
      'anusSymptoms',
      'hasFistulas'
    ];
  }

  nextQuestion = async () => {
    await sleep(this.duration / 2);
    if (this.#conditionalQuestionKeys.includes(this.currentQuestionKey)) {
      this.#clearChangeset(this.currentQuestionKey);
    }
    this.currentIndex++;
  };

  previousQuestion = async () => {
    await sleep(this.duration / 2);
    this.currentIndex--;
  };

  rules({ oldItems, newItems }) {
    if (oldItems[0] < newItems[0]) {
      return toLeft;
    }
    return toRight;
  }

  #clearChangeset = (currentQuestionKey: string): void => {
    switch (currentQuestionKey) {
      case 'immediateStool':
        if (!this.args.changeset.get('immediateStool')) {
          this.args.changeset.set('immediateStoolUrgency', undefined);
        }
        break;
      case 'bloodInStool':
        if (!this.args.changeset.get('bloodInStool')) {
          this.args.changeset.set('bloodInStoolFrequency', undefined);
        }
        break;
      case 'analIncontinence':
        if (!this.args.changeset.get('analIncontinence')) {
          this.args.changeset.set('analIncontinenceFrequency', undefined);
        }
        break;
      case 'articularPain':
        if (!this.args.changeset.get('articularPain')) {
          this.args.changeset.set('articularPainSeverity', undefined);
        }
        break;
      case 'eyeSymptoms':
        if (!this.args.changeset.get('eyeSymptoms')) {
          this.args.changeset.set('eyeSymptomsRedEye', undefined);
          this.args.changeset.set('eyeSymptomsPainful', undefined);
          this.args.changeset.set('eyeSymptomsSight', undefined);
        }
        break;
      case 'fever':
        if (!this.args.changeset.get('fever')) {
          this.args.changeset.set('feverLevel', undefined);
        }
        break;
      case 'abdominalPain':
        if (!this.args.changeset.get('abdominalPain')) {
          this.args.changeset.set('abdominalPainIntensity', undefined);
        }
        break;
      case 'anusSymptoms':
        if (!this.args.changeset.get('anusSymptoms')) {
          this.args.changeset.set('anusSymptomsFrequency', undefined);
        }
        break;
      case 'hasFistulas':
        if (!this.args.changeset.get('hasFistulas')) {
          this.args.changeset.set('fistulasPain', undefined);
          this.args.changeset.set('fistulasEmbarrassment', undefined);
          this.args.changeset.set('fistulasFlow', undefined);
          this.args.changeset.set('fistulasPainSkin', undefined);
          this.args.changeset.set('fistulasMovement', undefined);
        }
        break;
    }
  };

  <template>
    <AnimatedContainer>
      {{#AnimatedValue
        this.currentIndex rules=this.rules duration=this.duration
      }}
        <Form
          @data={{@changeset}}
          class='flex h-[90vh] flex-col justify-between p-8'
          id='mici-form'
          as |form|
        >
          <div class='flex flex-col gap-4'>
            {{#unless this.isOnSummary}}
              <div class='text-h3 text-pink'>
                {{yield to='title'}}
              </div>
            {{/unless}}
            {{#if this.isOnSummary}}
              <Summary
                @changeset={{@changeset}}
                @currentSections={{@currentSections}}
                @translationPrefix={{@translationPrefix}}
              />
            {{else}}
              <this.editionFormComponent
                @form={{form}}
                @onClick={{this.nextQuestion}}
              />
            {{/if}}
          </div>
          <div class='flex justify-end gap-2 pb-4'>
            <Button
              @icon='ArrowLeft'
              @variant='outlined'
              {{on 'click' this.previousQuestion}}
              disabled={{eq this.currentIndex 0}}
              data-test-left
            />
            {{#if this.isOnSummary}}
              <form.SubmitButton type='button' {{on 'click' @save}}>
                Sauvegarder
              </form.SubmitButton>
            {{else}}
              <Button
                @icon='ArrowRight'
                disabled={{eq
                  (changesetGet @changeset this.currentQuestionKey)
                  undefined
                }}
                {{on 'click' this.nextQuestion}}
                data-test-next
              >
                Suivant
              </Button>
            {{/if}}
          </div>
        </Form>
      {{/AnimatedValue}}
    </AnimatedContainer>
  </template>
}
